import { CSSProperties } from 'react';
import { cn, getColorway } from 'utils/styles';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import {
  CardsStackedItemBackwardsCompatibleProps,
  MediaAsset,
  DocumentWithEmbeddedEntries,
} from 'libs/contentful/props';
import { ButtonWithIconProps } from '@1debit/chimekit/dist/server/Button/Button';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import RichTextList from 'components/shared/RichTextList/RichTextList';
import BadgeOverlay from 'components/shared/BadgeOverlay/BadgeOverlay';
import { BackgroundColor, BadgeOverlayAlignment, CopyAlignment, BulletStyle } from 'types/constants';
import Media from '../shared/Media/Media';
import { CheckmarkHeavy } from '@1debit/icons';
import BackwardsCompatibleText from 'components/shared/BackwardsCompatibleText/BackwardsCompatibleText';

import { Block, Inline, TopLevelBlock } from '@contentful/rich-text-types';
import CTA from '../shared/CTA/CTA';

const hasUnorderedList = (doc: DocumentWithEmbeddedEntries | undefined): boolean => {
  if (!doc?.json?.content) return false;

  const checkNodes = (nodes: (TopLevelBlock | Block | Inline)[]): boolean => {
    for (const node of nodes) {
      if (node.nodeType === 'unordered-list') return true;
      if (node.content && Array.isArray(node.content)) {
        if (checkNodes(node.content! as (TopLevelBlock | Block | Inline)[])) return true;
      }
    }
    return false;
  };

  return checkNodes(doc.json.content);
};

interface Props extends CardsStackedItemBackwardsCompatibleProps, Pick<ButtonWithIconProps, 'iconProps'> {
  badgeOverlayAlignment?: BadgeOverlayAlignment;
  copyAlignment?: CopyAlignment;
  bulletStyle?: BulletStyle;
  iconAsset?: MediaAsset;
  isCheckmarkList?: React.ReactNode;
}

function CardsStackedItemImageV2Variant({
  sys,
  sectionName,
  desktopMedia,
  mobileMedia,
  eyebrow,
  eyebrowRte,
  headline,
  shortDescription,
  cta,
  color,
  topRightBadge,
  badgeOverlayAlignment = BadgeOverlayAlignment.Right,
  copyAlignment = CopyAlignment.Left,
  bulletStyle,
  iconAsset,
  desktopAnimation,
  mobileAnimation,
}: Props) {
  const styles = {
    '--image-url': !desktopAnimation?.assets && `url(${desktopMedia?.url})`,
    '--mobile-image-url': !mobileAnimation?.assets && `url(${mobileMedia?.url})`,
  } as CSSProperties;

  const isGradient = BackgroundColor.DarkGreenGradient === getColorway(color);

  const cardWrapperClasses = cn(
    // because gradients are also background images, we need to support multiple background images in the gradient case
    // this is useful for background images with transparent parts

    'scroll-mt-[var(--height-nav)] relative px-6 md:px-12 pt-3 md:pt-6 pb-10 md:pb-14 w-full min-h-[514px]',
    'bg-scroll bg-top bg-cover sm:h-[570px] md:min-h-[718px] bg-[image:var(--mobile-image-url)] sm:bg-[image:var(--image-url)]',
    'group group-data-[num-items=4]:md:basis-1/2-gap-6 rounded-[16px] md:rounded-[24px] active:rounded-[16px] md:active:rounded-[24px] active:bg-shade-transparent active:scale-100 overflow-hidden',
    'flex flex-col justify-end gap-6 md:gap-12 md:basis-1/3-gap-6 md:grow-1',
    'hover:text-inverse active:text-inverse active:bg-[image:var(--mobile-image-url)] sm:active:bg-[image:var(--image-url)]',
    {
      'bg-[image:var(--mobile-image-url),_var(--dark-green-gradient-mobile)] active:bg-[image:var(--mobile-image-url),_var(--dark-green-gradient-mobile)]':
        isGradient,
      'sm:bg-[image:var(--image-url),_var(--dark-green-gradient-desktop)] sm:active:bg-[image:var(--image-url),_var(--dark-green-gradient-desktop)]':
        isGradient,
    },
    getColorway(color),
  );

  const isOutlineButton = cta?.variant?.toLowerCase() === 'outline';

  const isCentered = copyAlignment === CopyAlignment.Centered;
  const isCheckmarkList = bulletStyle === BulletStyle.CheckBoxes && hasUnorderedList(shortDescription);

  return (
    <div
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      style={styles}
      className={cardWrapperClasses}
      data-testid="cardsStackedItemImageVariant">
      {topRightBadge && (
        <BadgeOverlay
          description={topRightBadge.image.description}
          sys={topRightBadge.sys}
          url={topRightBadge.image.url}
          fileName={topRightBadge.image.fileName}
          badgeOverlayAlignment={badgeOverlayAlignment}
        />
      )}

      {/* Lottie Animations Overlay */}
      {(desktopAnimation || mobileAnimation) && (
        <div className="rounded-lg">
          <Media desktopAnimation={desktopAnimation} mobileAnimation={mobileAnimation} />
        </div>
      )}

      <div className="w-full flex flex-col relative z-10">
        {eyebrow || eyebrowRte ? (
          <span
            className={cn(
              'font-sans font-medium text-eyebrow',
              'text-[var(--color-fern)] text-xs',
              'pb-0.5',
              isCentered ? 'text-center' : '',
            )}
            {...liveUpdateAttrs(sys?.id, 'eyebrow')}
            data-testid="eyebrow">
            <BackwardsCompatibleText text={eyebrow} richText={eyebrowRte} />
          </span>
        ) : null}
        {iconAsset && (
          <div {...liveUpdateAttrs(sys?.id, 'iconAsset')} data-testid="iconAsset" className="flex justify-center pb-6">
            <Media media={iconAsset} className="max-h-10 object-contain" />
          </div>
        )}
        {headline && (
          <div
            className={cn(
              'text-heading-md font-sans',
              'sm:text-heading-lg leading-[84%] sm:font-[570]',
              isCentered ? 'text-center' : '',
              iconAsset ? 'hidden' : '',
            )}
            {...liveUpdateAttrs(sys?.id, 'headline')}
            data-testid="headline">
            <RichTextHeadline headline={headline} level="h2" />
          </div>
        )}
        {shortDescription && (
          <div
            className={cn(
              'font-serif text-lg',
              'leading-[1.2]',
              // List-specific styles when using RichTextList with custom icon
              ...(isCheckmarkList
                ? [
                    'pt-12 sm:pt-10 md:pt-10 ml:pt-14 lg:pt-16',
                    'pb-8 sm:pb-10 md:pb-10 ml:pb-14 lg:pb-16',
                    '[&>ul]:flex [&>ul]:flex-col [&>ul]:items-center [&>ul]:font-sans [&>ul]:text-md ',
                    '[&>ul]:sm:grid [&>ul]:sm:grid-cols-2 [&>ul]:sm:gap-x-6 [&>ul]:sm:gap-y-2',
                    '[&>ul>li]:font-semibold [&>ul>li]:sm:text-[1.5rem] [&>ul>li]:ml:text-md [&>ul>li]:lg:text-[1.5rem]',
                    '[&>ul>li]:sm:justify-center [&>ul>li]:sm:!my-2',
                  ]
                : ['max-w-78 sm:max-w-[375px] md:max-w-78']),
              isCentered ? 'text-center mx-auto' : '',
            )}
            {...liveUpdateAttrs(sys?.id, 'shortDescription')}
            data-testid="shortDescription">
            {isCheckmarkList ? (
              <RichTextList
                item={shortDescription}
                arrowStyles="text-[var(--color-fern)]"
                customIcon={
                  <CheckmarkHeavy
                    color="var(--color-fern)"
                    size={14}
                    className="mt-1 -mr-1 [&_path]:!stroke-fern" // Fix for ChimeKit icon not changing the stroke based on the color prop
                  />
                }
              />
            ) : (
              <RichTextCopy copy={shortDescription} ySpacing="none" arrowStyles="text-[var(--color-fern)]" />
            )}
          </div>
        )}
      </div>
      {cta ? (
        <CTA
          cta={cta}
          variant="outline"
          className={cn(
            'place-self-center link-button-outline border-[1.5px] border-(--btn-outline)',
            'font-sans font-semibold flex items-center gap-2',
            {
              'link-button-outline border-[1.5px]': isOutlineButton,
              'py-[6.5px] px-14 rounded-md text-current hover:text-current leading-tight': isOutlineButton,
              'justify-center': CopyAlignment.Centered,
            },
          )}
        />
      ) : null}
    </div>
  );
}

export default CardsStackedItemImageV2Variant;
